import { Add, Cancel, SaveAs } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Slider,
  Typography,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import { ValidationErrors } from 'fluentvalidation-ts';
import React, { FC, useEffect, useState } from 'react';

import { valueToText } from '../../../../Common/CommonMethods';
import CustomButton, {
  CustomSelect,
  CustomTextField,
} from '../../../../Common/Components/Controls/InputControls';
import { useFormStyles as wrapperStyles } from '../../../../Common/Styles';
import { CardWrapper } from '../../Common/CommonComponents';
import { DialogOptions } from '../../Monitoring/Model/MonitoringModel';
import { validDMS, validDTUs } from '../Model/SqlServerData';
import {
  FailoverGroup,
  OperationType,
  SqlServer,
} from '../Model/SqlServerModel';
import failoverValidtionRules from '../Validation/failovergroupValidationRules.json';
import { FailoverGroupValidator } from '../Validation/FailoverGroupValidator';
import CopyToClipboard from 'react-copy-to-clipboard';
import { ThemedCopyIcon } from '../../../../Common/Components/Icons';
import { toastForAdminPasswordCopy } from '../Common/notification';

interface AddFailoverProps {
  sqlserver: SqlServer;
  failovergroup: FailoverGroup;
  setFailovergroup: (value: React.SetStateAction<FailoverGroup>) => void;
  regions: string[];
  open: boolean;
  handleDialogueOptions: (value: DialogOptions) => void;
  operationType: OperationType;
}

export const FailoverGroupForm: FC<AddFailoverProps> = ({
  sqlserver,
  failovergroup,
  setFailovergroup,
  regions,
  open,
  handleDialogueOptions,
  operationType,
}) => {
  const { classes: wrapper } = wrapperStyles();
  const [failoverErrors, setFailoverErrors] = useState<
    ValidationErrors<FailoverGroup>
  >({});

  const failoverGroupValidator = new FailoverGroupValidator();

  const validateFailoverGroup = () => {
    const failoverErrors = failoverGroupValidator.validate(failovergroup);
    if (sqlserver.ServerName === failovergroup.ServerName) {
      failoverErrors.ServerName = failoverValidtionRules.Server;
    }
    if (sqlserver.Region === failovergroup.Region) {
      failoverErrors.Region = failoverValidtionRules.Region;
    }
    setFailoverErrors(failoverErrors);
  };

  const validate = () => {
    validateFailoverGroup();
  };

  const handleSliderChange =
    (field: string) => (event: Event, value: number | number[]) => {
      setFailovergroup((prev) => ({ ...prev, [field]: value as number }));
    };

  useEffect(() => {
    validate();
  }, [failovergroup]);
  return (
    <>
      <Dialog
        disableEscapeKeyDown
        fullWidth
        maxWidth="xl"
        open={open}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">Add Failover Group</DialogTitle>
        <Divider className={wrapper.divider} />
        <DialogContent>
          <Grid2 container spacing={3}>
            <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6 }}>
              <CardWrapper>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                  Basic Informations
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid2 container spacing={2} alignItems="center">
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body1">Failover group name</Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <CustomTextField
                      error={!!failoverErrors.Name}
                      helperText={failoverErrors.Name}
                      value={failovergroup.Name}
                      placeholder="failover group name"
                      name="Name"
                      onChange={(e) =>
                        setFailovergroup((failovergroup) => ({
                          ...failovergroup,
                          Name: e.target.value,
                        }))
                      }
                      onBlur={validate}
                    />
                  </Grid2>
                </Grid2>
                <Grid2 container spacing={2} alignItems="center" mt={2}>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body1">Server Name</Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <CustomTextField
                      error={!!failoverErrors.ServerName}
                      helperText={failoverErrors.ServerName}
                      value={failovergroup.ServerName}
                      placeholder="Server Name"
                      name="ServerName"
                      onChange={(e) =>
                        setFailovergroup((failovergroup) => ({
                          ...failovergroup,
                          ServerName: e.target.value,
                        }))
                      }
                      onBlur={validate}
                    />
                  </Grid2>
                </Grid2>
                <Grid2 container spacing={2} alignItems="center" mt={2}>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body1"> User Name</Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <CustomTextField
                      error={!!failoverErrors.UserName}
                      helperText={failoverErrors.UserName}
                      value={failovergroup.UserName}
                      placeholder="User Name"
                      name="UserName"
                      onChange={(e) =>
                        setFailovergroup((failovergroup) => ({
                          ...failovergroup,
                          UserName: e.target.value,
                        }))
                      }
                      onBlur={validate}
                    />
                  </Grid2>
                </Grid2>

                <Grid2 container spacing={2} alignItems="center" mt={2}>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body1">Admin Password</Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <CustomTextField
                      disabled={true}
                      error={!!failoverErrors.AdminPassword}
                      helperText={failoverErrors.AdminPassword}
                      value={failovergroup.AdminPassword}
                      placeholder="Admin Password"
                      name="AdminPassword"
                      onChange={(e) =>
                        setFailovergroup((failovergroup) => ({
                          ...failovergroup,
                          AdminPassword: e.target.value,
                        }))
                      }
                      onBlur={validate}
                      type={'password'}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <CopyToClipboard
                              text={sqlserver.AdminPassword}
                              onCopy={toastForAdminPasswordCopy}
                            >
                              <IconButton size="small">
                                <ThemedCopyIcon fontSize="small" />
                              </IconButton>
                            </CopyToClipboard>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid2>
                </Grid2>
                <Grid2 container spacing={2} alignItems="center" mt={2}>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body1">Region</Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <CustomSelect
                      error={!!failoverErrors.Region}
                      helperText={failoverErrors.Region}
                      value={failovergroup.Region}
                      placeholder="Region"
                      name="Region"
                      onChange={(e) =>
                        setFailovergroup((failovergroup) => ({
                          ...failovergroup,
                          Region: e.target.value,
                        }))
                      }
                      onBlur={validate}
                      select
                      SelectProps={{
                        MenuProps: {
                          PaperProps: {
                            style: {
                              maxHeight: 350,
                            },
                          },
                        },
                      }}
                    >
                      {regions.map((region) => (
                        <MenuItem key={region} value={region}>
                          {region}
                        </MenuItem>
                      ))}
                    </CustomSelect>
                  </Grid2>
                </Grid2>
              </CardWrapper>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6 }}>
              <CardWrapper>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                  Additional Configuration
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid2 container spacing={2} alignItems="center">
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body1">Enable Elastic Pool</Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <Checkbox
                      sx={{ float: 'left' }}
                      id="Enable Elastic Pool"
                      value={failovergroup.EnableElasticPool ?? false}
                      checked={failovergroup.EnableElasticPool ?? false}
                      name="EnableElasticPool"
                      onChange={(e) =>
                        setFailovergroup((failovergroup) => ({
                          ...failovergroup,
                          EnableElasticPool: e.target.checked,
                        }))
                      }
                      onBlur={validate}
                    />
                  </Grid2>
                </Grid2>
                {failovergroup.EnableElasticPool && (
                  <Grid2>
                    <Grid2 container spacing={2} alignItems="center" mt={2}>
                      <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body1">eDTUs</Typography>
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Box>
                          <Slider
                            aria-label="Small steps"
                            defaultValue={failovergroup.EDtu}
                            getAriaValueText={valueToText}
                            step={null}
                            marks={validDTUs}
                            min={50}
                            max={3000}
                            onChange={handleSliderChange('EDtu')}
                            valueLabelDisplay="auto"
                          />
                          <CustomTextField
                            name="edtu"
                            error={!!failoverErrors.EDtu}
                            helperText={failoverErrors.EDtu}
                            value={failovergroup.EDtu}
                            onBlur={validate}
                          />
                        </Box>
                      </Grid2>
                    </Grid2>
                    <Grid2 container spacing={2} alignItems="center" mt={2}>
                      <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body1">
                          Data max size (GB)
                        </Typography>
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Box>
                          <Slider
                            aria-label="Small steps"
                            defaultValue={failovergroup.DataMaxSize}
                            getAriaValueText={valueToText}
                            marks={validDMS}
                            step={null}
                            min={50}
                            max={3000}
                            onChange={handleSliderChange('DataMaxSize')}
                            valueLabelDisplay="auto"
                          />
                          <CustomTextField
                            name="DataMaxSize"
                            error={!!failoverErrors.DataMaxSize}
                            helperText={failoverErrors.DataMaxSize}
                            value={failovergroup.DataMaxSize}
                            onBlur={validate}
                          />
                        </Box>
                      </Grid2>
                    </Grid2>
                    <Grid2 container spacing={2} alignItems="center" mt={2}>
                      <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body1">
                          Per database DTUs
                        </Typography>
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Box>
                          <Slider
                            aria-label="Small steps"
                            defaultValue={failovergroup.PerDbDtu}
                            getAriaValueText={valueToText}
                            step={null}
                            min={0}
                            max={failovergroup.EDtu}
                            marks={validDTUs}
                            onChange={handleSliderChange('PerDbDtu')}
                            valueLabelDisplay="auto"
                          />
                          <CustomTextField
                            name="PerDbDTU"
                            error={!!failoverErrors.PerDbDtu}
                            helperText={failoverErrors.PerDbDtu}
                            value={failovergroup.PerDbDtu}
                            onBlur={validate}
                          />
                        </Box>
                      </Grid2>
                    </Grid2>
                  </Grid2>
                )}
              </CardWrapper>
            </Grid2>
          </Grid2>
        </DialogContent>
        <Divider className={wrapper.divider} />
        <DialogActions>
          {operationType === OperationType.Edit ? (
            <CustomButton
              disabled={Object.keys(failoverErrors).length > 0}
              onClick={() => handleDialogueOptions(DialogOptions.Ok)}
              startIcon={<SaveAs />}
              label={OperationType[operationType]}
            />
          ) : (
            <CustomButton
              disabled={Object.keys(failoverErrors).length > 0}
              onClick={() => handleDialogueOptions(DialogOptions.Ok)}
              startIcon={<Add />}
              label={OperationType[operationType]}
            />
          )}

          <CustomButton
            onClick={() => handleDialogueOptions(DialogOptions.Cancel)}
            startIcon={<Cancel />}
            label="Cancel"
          />
        </DialogActions>
      </Dialog>
    </>
  );
};
